import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Col, Form, Modal, Row, Table } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioButton, NioCard, NioSection } from '../../../components';
import api, { getApiBase, getApiBaseOverride, setApiBaseOverride, clearApiBaseOverride, isAuthError } from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';
import ServiceForm from '../../admin/services/components/ServiceForm';

const SAV_TYPES_KEYS = [
  'reclamation', 'remboursement', 'retour-produit', 'garantie',
  'assistance-technique', 'suivi-intervention', 'support-client',
  'evaluation-prestataire', 'signalement-probleme', 'historique',
];

const SAV_STATUS_KEYS = ['pending', 'in-progress', 'resolved'];
const SAV_STATUS_VARIANTS = { pending: 'warning', 'in-progress': 'info', resolved: 'success' };

const SAV_PRIORITY_KEYS = ['low', 'medium', 'high'];
const SAV_PRIORITY_VARIANTS = { low: 'secondary', medium: 'primary', high: 'danger' };

function formatTicketDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('fr-FR');
  } catch {
    return '-';
  }
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const MENU_KEYS = ['users', 'stats', 'requests', 'categories', 'reviews', 'outreach', 'payments', 'support', 'settings', 'system'];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('users');

  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [services, setServices] = useState([]);

  const [contacts, setContacts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [providerReviews, setProviderReviews] = useState([]);

  // ===== SAV =====
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportStats, setSupportStats] = useState(null);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportFilterStatus, setSupportFilterStatus] = useState('all');
  const [supportFilterType, setSupportFilterType] = useState('all');
  const [openSupportTicket, setOpenSupportTicket] = useState(null);
  const [supportSaving, setSupportSaving] = useState(false);
  const [supportReply, setSupportReply] = useState('');
  const [supportAssign, setSupportAssign] = useState('');
  const [supportNextStatus, setSupportNextStatus] = useState('pending');
  const [supportNextPriority, setSupportNextPriority] = useState('medium');

  // ===== Site Settings =====
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [siteSettings, setSiteSettings] = useState({
    company: { name: '', tagline: '' },
    contact: { email: '', phone: '', address: '' },
    social: { facebook: '', instagram: '', linkedin: '', twitter: '', whatsapp: '' },
    footer: { copyright: '', termsUrl: '/terms-and-conditions', privacyUrl: '#' },
  });

  // ===== Système (serveur API + base de données) =====
  const [systemStatus, setSystemStatus] = useState(null);
  const [systemLoading, setSystemLoading] = useState(false);
  const [effectiveApiBase, setEffectiveApiBase] = useState(getApiBase());
  const [apiBaseIsOverride, setApiBaseIsOverride] = useState(Boolean(getApiBaseOverride()));
  const [apiBaseInput, setApiBaseInput] = useState(getApiBaseOverride());
  const [apiBaseTesting, setApiBaseTesting] = useState(false);
  const [apiBaseFeedback, setApiBaseFeedback] = useState(null);
  const [dbUriInput, setDbUriInput] = useState('');
  const [dbTesting, setDbTesting] = useState(false);
  const [dbApplying, setDbApplying] = useState(false);
  const [dbFeedback, setDbFeedback] = useState(null);

  // ===== Configuration plateforme (Stripe, e-mail, URLs, auth) =====
  const [platformConfig, setPlatformConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configInputs, setConfigInputs] = useState({}); // champ -> valeur saisie
  const [configClear, setConfigClear] = useState({}); // champ -> true = supprimer
  const [configSaving, setConfigSaving] = useState(false);
  const [configFeedback, setConfigFeedback] = useState(null);
  const [emailTestTo, setEmailTestTo] = useState('');
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailTestFeedback, setEmailTestFeedback] = useState(null);
  const [stripeTesting, setStripeTesting] = useState(false);
  const [stripeTestFeedback, setStripeTestFeedback] = useState(null);

  // ===== Paiements =====
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentFilterStatus, setPaymentFilterStatus] = useState('all');

  const [catsSaving, setCatsSaving] = useState(false);

  // ===== Service CRUD (merged from /admin/services) =====
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceSaving, setServiceSaving] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [newName, setNewName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [editName, setEditName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editRole, setEditRole] = useState('user');
  const [editVerificationStatus, setEditVerificationStatus] = useState('pending');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editVerificationNotes, setEditVerificationNotes] = useState('');

  const selectedUser = useMemo(
    () => users.find((u) => String(u._id) === String(selectedUserId)),
    [users, selectedUserId]
  );

  const manageableAccounts = useMemo(
    () => users.filter((u) => String(u.role || 'user').trim().toLowerCase() !== 'admin'),
    [users]
  );

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, requestsRes, servicesRes, contactsRes, blogsRes] = await Promise.allSettled([
        api.adminGetUsers(),
        api.getRequests({}),
        api.getServices({}),
        api.getContacts(),
        api.getBlogs(),
      ]);

      const authError = [usersRes, requestsRes, servicesRes, contactsRes, blogsRes]
        .filter((r) => r.status === 'rejected')
        .map((r) => r.reason)
        .find(isAuthError);
      if (authError) {
        redirectOnAuthError(authError);
        return;
      }

      if (usersRes.status === 'fulfilled' && usersRes.value?.success) {
        setUsers(Array.isArray(usersRes.value.data) ? usersRes.value.data : []);
      }
      if (requestsRes.status === 'fulfilled' && requestsRes.value?.success) {
        setRequests(Array.isArray(requestsRes.value.data) ? requestsRes.value.data : []);
      }
      if (servicesRes.status === 'fulfilled' && servicesRes.value?.success) {
        setServices(Array.isArray(servicesRes.value.data) ? servicesRes.value.data : []);
      }
      if (contactsRes.status === 'fulfilled' && contactsRes.value?.success) {
        setContacts(Array.isArray(contactsRes.value.data) ? contactsRes.value.data : []);
      }
      if (blogsRes.status === 'fulfilled' && blogsRes.value?.success) {
        setBlogs(Array.isArray(blogsRes.value.data) ? blogsRes.value.data : []);
      }
    } catch (e) {
      setError(e.message || t('admin.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSupport = async () => {
    setSupportLoading(true);
    try {
      const [ticketsRes, statsRes] = await Promise.allSettled([
        api.adminGetSupportTickets(),
        api.adminGetSupportStats(),
      ]);
      const authError = [ticketsRes, statsRes]
        .filter((r) => r.status === 'rejected')
        .map((r) => r.reason)
        .find(isAuthError);
      if (authError) {
        redirectOnAuthError(authError);
        return;
      }
      if (ticketsRes.status === 'fulfilled' && ticketsRes.value?.success) {
        setSupportTickets(Array.isArray(ticketsRes.value.data) ? ticketsRes.value.data : []);
      }
      if (statsRes.status === 'fulfilled' && statsRes.value?.success) {
        setSupportStats(statsRes.value.data || null);
      }
    } catch (e) {
      setError(e.message || t('admin.savLoadFailed'));
    } finally {
      setSupportLoading(false);
    }
  };

  const fetchSiteSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await api.adminGetSiteSettings();
      if (res?.success && res.data) {
        setSiteSettings((prev) => ({
          company: { ...prev.company, ...(res.data.company || {}) },
          contact: { ...prev.contact, ...(res.data.contact || {}) },
          social: { ...prev.social, ...(res.data.social || {}) },
          footer: { ...prev.footer, ...(res.data.footer || {}) },
        }));
      }
    } catch (e) {
      if (redirectOnAuthError(e)) return;
      setError(e.message || t('admin.settings.loadFailed'));
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSiteSettings = async () => {
    setSettingsSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.adminSaveSiteSettings(siteSettings);
      if (res?.success) {
        setSuccess(t('admin.settings.saved'));
      }
    } catch (e) {
      setError(e.message || t('admin.settings.saveFailed'));
    } finally {
      setSettingsSaving(false);
    }
  };

  // ===== Système =====
  const fetchSystemStatus = async () => {
    setSystemLoading(true);
    try {
      const res = await api.adminGetSystemStatus();
      setSystemStatus(res?.success ? res.data : null);
    } catch (e) {
      if (redirectOnAuthError(e)) return;
      setSystemStatus(null);
    } finally {
      setSystemLoading(false);
    }
  };

  const refreshApiBaseState = () => {
    setEffectiveApiBase(getApiBase());
    setApiBaseIsOverride(Boolean(getApiBaseOverride()));
  };

  // ===== Configuration plateforme =====
  const fetchPlatformConfig = async () => {
    setConfigLoading(true);
    try {
      const res = await api.adminGetConfig();
      if (res?.success) {
        setPlatformConfig(res.data);
        // Initialise les champs non-secrets avec leur valeur courante.
        const init = {};
        Object.entries(res.data?.fields || {}).forEach(([key, meta]) => {
          init[key] = meta.secret ? '' : (meta.value || '');
        });
        setConfigInputs(init);
        setConfigClear({});
      }
    } catch (e) {
      if (redirectOnAuthError(e)) return;
      setPlatformConfig(null);
    } finally {
      setConfigLoading(false);
    }
  };

  const CONFIG_GROUPS = [
    { key: 'stripe', titleKey: 'admin.config.stripeTitle', hintKey: 'admin.config.stripeHint',
      fields: ['STRIPE_SECRET_KEY', 'STRIPE_PUBLISHABLE_KEY', 'STRIPE_WEBHOOK_SECRET', 'STRIPE_CURRENCY', 'STRIPE_TLS_REJECT_UNAUTHORIZED'] },
    { key: 'email', titleKey: 'admin.config.emailTitle', hintKey: 'admin.config.emailHint',
      fields: ['BREVO_API_KEY', 'BREVO_SMTP_KEY', 'BREVO_SMTP_LOGIN', 'BREVO_SENDER_EMAIL', 'BREVO_SENDER_NAME', 'EMAIL_FROM'] },
    { key: 'smtp', titleKey: 'admin.config.smtpTitle', hintKey: 'admin.config.smtpHint',
      fields: ['SMTP_HOST', 'SMTP_PORT', 'SMTP_USER', 'SMTP_PASS', 'SMTP_TLS_REJECT_UNAUTHORIZED'] },
    { key: 'urls', titleKey: 'admin.config.urlsTitle', hintKey: 'admin.config.urlsHint',
      fields: ['FRONTEND_URL', 'PUBLIC_BACKEND_URL', 'CORS_ORIGINS'] },
    { key: 'auth', titleKey: 'admin.config.authTitle', hintKey: 'admin.config.authHint',
      fields: ['JWT_SECRET', 'AUTH_TOKEN_TTL'] },
  ];

  const savePlatformConfig = async () => {
    setConfigSaving(true);
    setConfigFeedback(null);
    try {
      const fields = platformConfig?.fields || {};
      const values = {};
      const clearKeys = new Set(Object.keys(configClear).filter((k) => configClear[k]));

      Object.entries(configInputs).forEach(([key, raw]) => {
        const meta = fields[key];
        if (!meta) return;
        const trimmed = String(raw ?? '').trim();
        if (meta.secret) {
          // secret : vide = inchangé (sauf si case « supprimer » cochée)
          if (trimmed && !clearKeys.has(key)) values[key] = trimmed;
        } else if (trimmed !== String(meta.value || '').trim()) {
          // champ visible modifié : vide = suppression de la variable
          if (trimmed) values[key] = trimmed;
          else clearKeys.add(key);
        }
      });

      if (!Object.keys(values).length && !clearKeys.size) {
        setConfigFeedback({ ok: false, message: t('admin.config.nothingToSave') });
        return;
      }

      const res = await api.adminSaveConfig({ values, clearKeys: [...clearKeys] });
      if (res?.success) {
        setPlatformConfig(res.data);
        const init = {};
        Object.entries(res.data?.fields || {}).forEach(([key, meta]) => {
          init[key] = meta.secret ? '' : (meta.value || '');
        });
        setConfigInputs(init);
        setConfigClear({});
        const warns = Object.values(res.warnings || {});
        setConfigFeedback({
          ok: true,
          message: [t('admin.config.saved'), ...warns].join(' '),
        });
        fetchSystemStatus();
      } else {
        const firstErr = res?.fieldErrors ? Object.values(res.fieldErrors)[0] : null;
        setConfigFeedback({ ok: false, message: firstErr || res?.message || t('admin.config.saveFailed') });
      }
    } catch (e) {
      setConfigFeedback({ ok: false, message: e.message || t('admin.config.saveFailed') });
    } finally {
      setConfigSaving(false);
    }
  };

  const testEmailConfig = async () => {
    setEmailTesting(true);
    setEmailTestFeedback(null);
    try {
      const res = await api.adminTestEmail(emailTestTo.trim() || undefined);
      setEmailTestFeedback(
        res?.success
          ? { ok: true, message: res.message || t('admin.config.testEmailOk') }
          : { ok: false, message: res?.message || t('admin.config.testEmailFail') },
      );
    } catch (e) {
      setEmailTestFeedback({ ok: false, message: e.message || t('admin.config.testEmailFail') });
    } finally {
      setEmailTesting(false);
    }
  };

  const testStripeConfig = async () => {
    setStripeTesting(true);
    setStripeTestFeedback(null);
    try {
      const res = await api.adminTestStripe();
      setStripeTestFeedback(
        res?.success
          ? { ok: true, message: `${res.message}${res.livemode != null ? ` (${res.livemode ? 'live' : 'test'})` : ''}` }
          : { ok: false, message: res?.message || t('admin.config.testStripeFail') },
      );
    } catch (e) {
      setStripeTestFeedback({ ok: false, message: e.message || t('admin.config.testStripeFail') });
    } finally {
      setStripeTesting(false);
    }
  };

  const testApiBaseUrl = async () => {
    setApiBaseTesting(true);
    setApiBaseFeedback(null);
    const res = await api.testApiBase(apiBaseInput);
    setApiBaseFeedback(
      res.success
        ? { ok: true, message: `${t('admin.system.apiTestOk')} (${res.latencyMs} ms)` }
        : { ok: false, message: `${t('admin.system.apiTestFail')} — ${res.message}` },
    );
    setApiBaseTesting(false);
  };

  const applyApiBase = () => {
    const normalized = setApiBaseOverride(apiBaseInput);
    if (!normalized) {
      setApiBaseFeedback({ ok: false, message: t('admin.system.apiInvalid') });
      return;
    }
    setApiBaseInput(normalized);
    refreshApiBaseState();
    setApiBaseFeedback({ ok: true, message: t('admin.system.apiApplied') });
  };

  const resetApiBase = () => {
    clearApiBaseOverride();
    setApiBaseInput('');
    refreshApiBaseState();
    setApiBaseFeedback({ ok: true, message: t('admin.system.apiResetDone') });
  };

  const testDatabaseUri = async () => {
    setDbTesting(true);
    setDbFeedback(null);
    try {
      const res = await api.adminTestDatabase(dbUriInput);
      setDbFeedback(
        res?.success
          ? { ok: true, message: `${t('admin.system.dbTestOk')} — ${res.data.host}/${res.data.name} (${res.data.latencyMs} ms)` }
          : { ok: false, message: `${t('admin.system.dbTestFail')} — ${res?.message || ''}` },
      );
    } catch (e) {
      setDbFeedback({ ok: false, message: `${t('admin.system.dbTestFail')} — ${e.message}` });
    } finally {
      setDbTesting(false);
    }
  };

  const applyDatabaseUri = async () => {
    if (!window.confirm(t('admin.system.dbApplyConfirm'))) return;
    setDbApplying(true);
    setDbFeedback(null);
    try {
      const res = await api.adminUpdateDatabase(dbUriInput);
      if (res?.success) {
        setDbFeedback({ ok: true, message: res.warning || t('admin.system.dbApplied') });
        setDbUriInput('');
        fetchSystemStatus();
      } else {
        setDbFeedback({ ok: false, message: res?.message || t('admin.system.dbTestFail') });
      }
    } catch (e) {
      setDbFeedback({ ok: false, message: e.message });
    } finally {
      setDbApplying(false);
    }
  };

  useEffect(() => {
    if (activeSection === 'support') {
      fetchSupport();
    }
    if (activeSection === 'payments') {
      fetchPayments();
    }
    if (activeSection === 'settings') {
      fetchSiteSettings();
    }
    if (activeSection === 'system') {
      fetchSystemStatus();
      fetchPlatformConfig();
      setApiBaseInput(getApiBaseOverride());
      refreshApiBaseState();
    }
    if (activeSection === 'reviews') {
      setReviewsLoading(true);
      Promise.allSettled([
        fetchSupport(),
        api.adminGetProviderReviews(),
      ])
        .then(([, reviewsRes]) => {
          if (reviewsRes.status === 'rejected' && redirectOnAuthError(reviewsRes.reason)) return;
          if (reviewsRes.status === 'fulfilled' && reviewsRes.value?.success) {
            setProviderReviews(Array.isArray(reviewsRes.value.data) ? reviewsRes.value.data : []);
          }
        })
        .catch(() => {})
        .finally(() => setReviewsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try {
      const res = await api.adminGetPayments();
      if (res?.success) setPayments(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (redirectOnAuthError(e)) return;
      setError(e.message || t('admin.paymentsLoadFailed'));
    } finally {
      setPaymentsLoading(false);
    }
  };

  const filteredPayments = useMemo(() => {
    if (paymentFilterStatus === 'all') return payments;
    return payments.filter((p) => p.status === paymentFilterStatus);
  }, [payments, paymentFilterStatus]);

  const paymentStats = useMemo(() => {
    const completed = payments.filter((p) => p.status === 'completed');
    const total = completed.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    return {
      count: payments.length,
      completed: completed.length,
      pending: payments.filter((p) => p.status === 'pending').length,
      failed: payments.filter((p) => p.status === 'failed' || p.status === 'expired').length,
      total,
    };
  }, [payments]);

  const filteredSupportTickets = useMemo(() => {
    return supportTickets.filter((t) => {
      if (supportFilterStatus !== 'all' && t.status !== supportFilterStatus) return false;
      if (supportFilterType !== 'all' && t.type !== supportFilterType) return false;
      return true;
    });
  }, [supportTickets, supportFilterStatus, supportFilterType]);

  const requestsWithReviews = useMemo(
    () =>
      requests.filter((r) => {
        const rating = r.averageRating != null && String(r.averageRating).trim() !== '';
        const comments = r.clientComments != null && String(r.clientComments).trim() !== '';
        return rating || comments;
      }),
    [requests],
  );

  const evaluationTickets = useMemo(
    () =>
      supportTickets.filter((t) => {
        if (t.type === 'evaluation-prestataire') return true;
        const n = Number(t.rating);
        return Number.isFinite(n) && n > 0;
      }),
    [supportTickets],
  );

  const openSupportDetail = async (ticket) => {
    try {
      const res = await api.getSupportTicket(ticket._id);
      const data = res?.success ? res.data : ticket;
      setOpenSupportTicket(data);
      setSupportAssign(data.assignedTo || '');
      setSupportNextStatus(data.status || 'pending');
      setSupportNextPriority(data.priority || 'medium');
      setSupportReply('');
    } catch {
      setOpenSupportTicket(ticket);
      setSupportAssign(ticket.assignedTo || '');
      setSupportNextStatus(ticket.status || 'pending');
      setSupportNextPriority(ticket.priority || 'medium');
      setSupportReply('');
    }
  };

  const closeSupportDetail = () => {
    setOpenSupportTicket(null);
    setSupportReply('');
  };

  const saveSupportTicket = async () => {
    if (!openSupportTicket?._id) return;
    setSupportSaving(true);
    try {
      const res = await api.adminUpdateSupportTicket(openSupportTicket._id, {
        status: supportNextStatus,
        priority: supportNextPriority,
        assignedTo: supportAssign ? String(supportAssign).trim() : undefined,
      });
      if (res?.success) {
        setOpenSupportTicket(res.data);
        await fetchSupport();
        setSuccess(t('admin.ticketUpdated'));
      }
    } catch (e) {
      setError(e.message || t('admin.ticketUpdateFailed'));
    } finally {
      setSupportSaving(false);
    }
  };

  const sendSupportReply = async () => {
    if (!openSupportTicket?._id || !supportReply.trim()) return;
    setSupportSaving(true);
    try {
      const res = await api.addSupportTicketMessage(openSupportTicket._id, {
        senderRole: 'admin',
        senderName: 'Admin support',
        content: supportReply.trim(),
      });
      if (res?.success) {
        setOpenSupportTicket(res.data);
        setSupportReply('');
        await fetchSupport();
      }
    } catch (e) {
      setError(e.message || t('admin.replyFailed'));
    } finally {
      setSupportSaving(false);
    }
  };

  const deleteSupportTicket = async (ticket) => {
    if (!ticket?._id) return;
    const ok = window.confirm(t('admin.deleteTicketConfirm'));
    if (!ok) return;
    setSupportSaving(true);
    try {
      await api.adminDeleteSupportTicket(ticket._id);
      if (openSupportTicket?._id === ticket._id) closeSupportDetail();
      await fetchSupport();
      setSuccess(t('admin.ticketDeleted'));
    } catch (e) {
      setError(e.message || t('admin.deleteFailed'));
    } finally {
      setSupportSaving(false);
    }
  };

  // Session morte côté serveur (jeton expiré/révoqué) : on purge le
  // localStorage et on renvoie vers la page de connexion.
  const redirectOnAuthError = (err) => {
    if (!isAuthError(err)) return false;
    localStorage.removeItem('user');
    navigate('/auth/login', { replace: true });
    return true;
  };

  useEffect(() => {
    const user = getStoredUser();
    if (!user?.token) {
      // Session stockée sans jeton (antérieure au JWT) : inutilisable pour l'API.
      localStorage.removeItem('user');
      navigate('/auth/login', { replace: true });
      return;
    }
    if (user.role !== 'admin') {
      navigate('/profile', { replace: true });
      return;
    }
    fetchAll();
  }, [navigate]);

  const roleLabel = (role) => {
    const r = String(role || '').trim().toLowerCase();
    if (r === 'user') return t('admin.users.user');
    if (r === 'provider') return t('admin.users.provider');
    if (r === 'admin') return 'admin';
    return role || '-';
  };

  const iaBadge = (status) => {
    const s = String(status || '').trim().toLowerCase();
    if (s === 'approved') return <span className="badge bg-success">IA verified</span>;
    if (s === 'rejected') return <span className="badge bg-danger">IA rejected</span>;
    if (s === 'pending') return <span className="badge bg-warning text-dark">IA pending</span>;
    return <span className="text-muted">-</span>;
  };

  const openEditForm = (u) => {
    setSelectedUserId(u._id);
    setEditName(u.name ? String(u.name) : '');
    setEditLastName(u.lastName ? String(u.lastName) : '');
    setEditEmail(u.email ? String(u.email) : '');
    setEditPhone(u.phone ? String(u.phone) : '');
    setEditRole(String(u.role || 'user').trim().toLowerCase() || 'user');
    setEditVerificationStatus(String(u.verificationStatus || 'pending').trim().toLowerCase());
    setEditIsActive(u.isActive !== false);
    setEditVerificationNotes(u.verificationNotes ? String(u.verificationNotes) : '');
    setError('');
    setSuccess('');
  };

  const cancelEdit = () => {
    setSelectedUserId('');
    setEditName('');
    setEditLastName('');
    setEditEmail('');
    setEditPhone('');
    setEditRole('user');
    setEditVerificationStatus('pending');
    setEditIsActive(true);
    setEditVerificationNotes('');
  };

  const saveEditForm = async () => {
    if (!selectedUser?._id) return;
    const confirmed = window.confirm(t('admin.saveConfirm'));
    if (!confirmed) return;

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const nextStatus = String(editVerificationStatus || 'pending').trim().toLowerCase();
      const basePayload = {
        name: editName ? String(editName).trim() : undefined,
        lastName: editLastName ? String(editLastName).trim() : undefined,
        email: editEmail ? String(editEmail).trim().toLowerCase() : undefined,
        phone: editPhone ? String(editPhone).trim() : undefined,
        role: editRole,
        isActive: editIsActive,
      };
      if (String(selectedUser.role || '').trim().toLowerCase() === 'provider') {
        await api.adminUpdateProviderVerification(selectedUser._id, {
          verificationStatus: nextStatus,
          verificationNotes: editVerificationNotes ? String(editVerificationNotes).trim() : undefined,
        });
        await api.adminUpdateUser(selectedUser._id, basePayload);
      } else {
        await api.adminUpdateUser(selectedUser._id, {
          ...basePayload,
          verificationStatus: nextStatus,
          verificationNotes: editVerificationNotes ? String(editVerificationNotes).trim() : undefined,
        });
      }
      await fetchAll();
      setSuccess(t('admin.accountUpdated'));
    } catch (e) {
      setError(e.message || t('admin.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (u) => {
    const ok = window.confirm(t('admin.deleteUserConfirm', { email: u.email }));
    if (!ok) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.adminDeleteUser(u._id);
      await fetchAll();
      if (String(selectedUserId) === String(u._id)) cancelEdit();
      setSuccess(t('admin.userDeleted'));
    } catch (e) {
      setError(e.message || t('admin.deleteFailed2'));
    } finally {
      setSaving(false);
    }
  };

  const createUser = async () => {
    if (!newName.trim() || !newEmail.trim() || !newPassword) {
      setError(t('admin.createFieldsRequired'));
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.adminCreateUser({
        name: newName.trim(),
        lastName: newLastName.trim() || undefined,
        email: newEmail.trim().toLowerCase(),
        password: newPassword,
        phone: newPhone.trim() || undefined,
        role: newRole,
        isActive: true,
        verificationStatus: 'approved',
      });
      setNewName('');
      setNewLastName('');
      setNewEmail('');
      setNewPassword('');
      setNewPhone('');
      setNewRole('user');
      await fetchAll();
      setSuccess(t('admin.userAdded'));
    } catch (e) {
      setError(e.message || t('admin.userAddFailed'));
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    const usersCount = users.length;
    const jobsCount = requests.length;
    const completedCount = requests.filter((r) => String(r.status || '').toLowerCase() === 'completed').length;
    const revenue = requests.reduce((sum, r) => {
      const candidates = [r.hourlyPrice, r.servicePrice, r.customQuote];
      const raw = candidates.find((x) => x != null && String(x).trim() !== '');
      if (!raw) return sum;
      const normalized = String(raw).replace(/[^\d.,-]/g, '').replace(',', '.');
      const n = Number(normalized);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
    return { usersCount, jobsCount, completedCount, revenue };
  }, [users, requests]);

  const renderUsersSection = () => (
    <>
      <div className="mb-3 d-flex flex-column flex-md-row justify-content-between align-items-start gap-2">
        <h5 className="mb-0">{t('admin.users.listTitle')}</h5>
        <div className="d-flex flex-wrap gap-2">
          <NioButton
            label={saving ? '...' : t('admin.users.seedMissing')}
            className="btn-outline-primary btn-sm"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              setError('');
              setSuccess('');
              try {
                const res = await api.adminSeedDefaultUsers(false);
                await fetchAll();
                setSuccess(res?.message || t('admin.users.demoUpdated'));
              } catch (e) {
                setError(e.message || t('admin.users.demoCreateFailed'));
              } finally {
                setSaving(false);
              }
            }}
          />
          <NioButton
            label={saving ? '...' : t('admin.users.recreateDemo')}
            className="btn-outline-danger btn-sm"
            disabled={saving}
            onClick={async () => {
              const ok = window.confirm(
                t('admin.users.seedConfirm')
              );
              if (!ok) return;
              setSaving(true);
              setError('');
              setSuccess('');
              try {
                const res = await api.adminSeedDefaultUsers(true);
                await fetchAll();
                setSuccess(res?.message || t('admin.users.demoRecreated'));
              } catch (e) {
                setError(e.message || t('admin.users.demoRecreateFailed'));
              } finally {
                setSaving(false);
              }
            }}
          />
        </div>
      </div>
      <NioCard className="shadow-sm mb-3 bg-light">
        <NioCard.Body className="p-3 small text-muted">
          <strong className="text-dark">{t('admin.users.refAccounts')}</strong>{' '}
          {t('admin.users.refAccountsDesc')}
        </NioCard.Body>
      </NioCard>
      {showAddForm && (
        <NioCard className="shadow-sm mb-3">
          <NioCard.Body className="p-3 p-lg-4">
            <h6 className="mb-3">{t('admin.users.addUser')}</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('admin.users.firstName')}</Form.Label>
                  <Form.Control value={newName} onChange={(e) => setNewName(e.target.value)} disabled={saving} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('admin.users.lastName')}</Form.Label>
                  <Form.Control value={newLastName} onChange={(e) => setNewLastName(e.target.value)} disabled={saving} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('admin.users.role')}</Form.Label>
                  <Form.Select value={newRole} onChange={(e) => setNewRole(e.target.value)} disabled={saving}>
                    <option value="user">{t('admin.users.user')}</option>
                    <option value="provider">{t('admin.users.provider')}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('admin.users.email')}</Form.Label>
                  <Form.Control type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} disabled={saving} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('admin.users.password')}</Form.Label>
                  <Form.Control type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={saving} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('admin.users.phone')}</Form.Label>
                  <Form.Control value={newPhone} onChange={(e) => setNewPhone(e.target.value)} disabled={saving} />
                </Form.Group>
              </Col>
            </Row>
            <NioButton onClick={createUser} label={saving ? t('admin.users.adding') : t('admin.users.addUserBtn')} className="btn-primary btn-sm" disabled={saving} />
          </NioCard.Body>
        </NioCard>
      )}
      <Table responsive bordered hover>
        <thead>
          <tr>
            <th>{t('admin.users.thName')}</th>
            <th>{t('admin.users.thEmail')}</th>
            <th>{t('admin.users.thPhone')}</th>
            <th>{t('admin.users.thRole')}</th>
            <th>{t('admin.users.thActive')}</th>
            <th>{t('admin.users.thVerifStatus')}</th>
            <th>{t('admin.users.thIaDocs')}</th>
            <th>{t('admin.users.thActions')}</th>
          </tr>
        </thead>
        <tbody>
          {manageableAccounts.map((u) => (
            <tr key={u._id}>
              <td>{[u.name, u.lastName].filter(Boolean).join(' ') || '-'}</td>
              <td>{u.email || '-'}</td>
              <td>{u.phone || '-'}</td>
              <td>{roleLabel(u.role)}</td>
              <td className="text-center"><Form.Check type="checkbox" checked={u.isActive !== false} disabled /></td>
              <td>{String(u.verificationStatus || 'pending').toLowerCase()}</td>
              <td>{iaBadge(u.verificationDocumentsAiVerification?.status)}</td>
              <td className="d-flex gap-2">
                <NioButton
                  onClick={() => setShowAddForm((prev) => !prev)}
                  label={showAddForm ? t('admin.users.closeAdd') : t('admin.users.add')}
                  className="btn-outline-secondary btn-sm"
                  disabled={saving}
                />
                <NioButton onClick={() => openEditForm(u)} label={t('admin.users.edit')} className="btn-outline-primary btn-sm" disabled={saving} />
                <NioButton onClick={() => deleteUser(u)} label={t('admin.users.delete')} className="btn-outline-danger btn-sm" disabled={saving} />
                  
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <NioCard className="shadow-sm mt-3">
        <NioCard.Body className="p-3 p-lg-4">
          <h6 className="mb-3">{t('admin.users.editFormTitle')}</h6>
          {!selectedUser ? (
            <div className="text-muted">{t('admin.users.clickEdit')}</div>
          ) : (
            <>
              <div className="mb-2"><strong>{t('admin.users.account')}</strong> {[selectedUser.name, selectedUser.lastName].filter(Boolean).join(' ') || selectedUser.email}</div>
              <div className="mb-2"><strong>{t('admin.users.email')}:</strong> {selectedUser.email}</div>
              <div className="mb-3"><strong>{t('admin.users.role')}:</strong> {roleLabel(selectedUser.role)}</div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.users.firstName')}</Form.Label>
                    <Form.Control value={editName} onChange={(e) => setEditName(e.target.value)} disabled={saving} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.users.lastName')}</Form.Label>
                    <Form.Control value={editLastName} onChange={(e) => setEditLastName(e.target.value)} disabled={saving} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.users.email')}</Form.Label>
                    <Form.Control type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} disabled={saving} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.users.phone')}</Form.Label>
                    <Form.Control value={editPhone} onChange={(e) => setEditPhone(e.target.value)} disabled={saving} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.users.role')}</Form.Label>
                    <Form.Select value={editRole} onChange={(e) => setEditRole(e.target.value)} disabled={saving}>
                      <option value="user">{t('admin.users.user')}</option>
                      <option value="provider">{t('admin.users.provider')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.users.verifStatus')}</Form.Label>
                <Form.Select value={editVerificationStatus} onChange={(e) => setEditVerificationStatus(e.target.value)} disabled={saving}>
                  <option value="pending">pending</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.users.notes')}</Form.Label>
                <Form.Control as="textarea" rows={3} value={editVerificationNotes} onChange={(e) => setEditVerificationNotes(e.target.value)} disabled={saving} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check type="checkbox" label={t('admin.users.accountActive')} checked={editIsActive} onChange={(e) => setEditIsActive(e.target.checked)} disabled={saving} />
              </Form.Group>
              <div className="d-flex gap-2">
                <NioButton onClick={saveEditForm} label={saving ? t('admin.users.saving') : t('admin.users.save')} className="btn-primary btn-sm" disabled={saving} />
                <NioButton onClick={cancelEdit} label={t('admin.users.cancel')} className="btn-outline-secondary btn-sm" disabled={saving} />
              </div>
            </>
          )}
        </NioCard.Body>
      </NioCard>
    </>
  );

  const renderStatsSection = () => (
    <Row className="gy-3">
      <Col md={6} xl={3}><NioCard><NioCard.Body className="p-3"><div className="text-muted small">{t('admin.stats.users')}</div><h4>{stats.usersCount}</h4></NioCard.Body></NioCard></Col>
      <Col md={6} xl={3}><NioCard><NioCard.Body className="p-3"><div className="text-muted small">{t('admin.stats.jobs')}</div><h4>{stats.jobsCount}</h4></NioCard.Body></NioCard></Col>
      <Col md={6} xl={3}><NioCard><NioCard.Body className="p-3"><div className="text-muted small">{t('admin.stats.completed')}</div><h4>{stats.completedCount}</h4></NioCard.Body></NioCard></Col>
      <Col md={6} xl={3}><NioCard><NioCard.Body className="p-3"><div className="text-muted small">{t('admin.stats.revenue')}</div><h4>{stats.revenue.toFixed(0)} DT</h4></NioCard.Body></NioCard></Col>
    </Row>
  );

  const renderRequestsSection = () => (
    <>
      <h5 className="mb-3">{t('admin.requests.title')}</h5>
      <Table responsive bordered hover>
        <thead>
          <tr>
            <th>{t('admin.requests.thClient')}</th>
            <th>{t('admin.requests.thService')}</th>
            <th>{t('admin.requests.thSubService')}</th>
            <th>{t('admin.requests.thStatus')}</th>
            <th>{t('admin.requests.thPrice')}</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r._id}>
              <td>{r.userEmail || r.userName || '-'}</td>
              <td>{Array.isArray(r.services) && r.services[0] ? r.services[0].title : '-'}</td>
              <td>{r.subService || '-'}</td>
              <td>{r.status || '-'}</td>
              <td>{r.servicePrice || r.hourlyPrice || r.customQuote || '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );

  const openAddServiceForm = () => {
    setEditingService(null);
    setShowServiceForm(true);
  };

  const openEditServiceForm = (service) => {
    setEditingService(service);
    setShowServiceForm(true);
  };

  const closeServiceForm = () => {
    setEditingService(null);
    setShowServiceForm(false);
  };

  const submitServiceForm = async (serviceData) => {
    setServiceSaving(true);
    setError('');
    setSuccess('');
    try {
      if (editingService?._id) {
        const res = await api.updateService(editingService._id, serviceData);
        if (!res?.success) throw new Error(res?.message || t('admin.services.form.updating'));
        setSuccess(t('admin.services.updateSuccess'));
      } else {
        const res = await api.createService(serviceData);
        if (!res?.success) throw new Error(res?.message || t('admin.services.form.creating'));
        setSuccess(t('admin.services.createSuccess'));
      }
      await fetchAll();
      closeServiceForm();
    } catch (e) {
      setError(e.message || t('admin.categories.editFailed'));
    } finally {
      setServiceSaving(false);
    }
  };

  const deleteServiceDoc = async (service) => {
    if (!service?._id) return;
    if (!window.confirm(t('admin.services.deleteConfirm'))) return;
    setServiceSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.deleteService(service._id);
      if (!res?.success) throw new Error(res?.message || t('admin.categories.deleteFailed'));
      setSuccess(t('admin.services.deleteSuccess'));
      await fetchAll();
    } catch (e) {
      setError(e.message || t('admin.categories.deleteFailed'));
    } finally {
      setServiceSaving(false);
    }
  };

  const toggleServiceActive = async (service) => {
    if (!service?._id) return;
    setServiceSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.updateService(service._id, { isActive: service.isActive === false });
      if (!res?.success) throw new Error(res?.message || t('admin.categories.editFailed'));
      await fetchAll();
    } catch (e) {
      setError(e.message || t('admin.categories.editFailed'));
    } finally {
      setServiceSaving(false);
    }
  };

  const renderCategoriesSection = () => (
    <>
      {/* Page header */}
      <div className="mb-4">
        <h4 className="mb-2">{t('admin.categories.title')}</h4>
        <p className="text-muted mb-0">{t('admin.categories.desc')}</p>
      </div>

      {/* Section 1: Service overview */}
      <NioCard className="shadow-sm mb-4">
        <NioCard.Body className="p-3 p-lg-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-3">
            <div>
              <h6 className="mb-1">{t('admin.categories.overviewTitle')}</h6>
              <p className="text-muted small mb-0">{t('admin.categories.overviewDesc')}</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <NioButton
                label={t('admin.services.addNew')}
                className="btn-primary btn-sm"
                onClick={openAddServiceForm}
                disabled={loading || serviceSaving}
              />
              <NioButton
                label={loading ? t('admin.loading') : t('admin.categories.refresh')}
                className="btn-outline-primary btn-sm"
                onClick={() => fetchAll()}
                disabled={loading || catsSaving}
              />
              <NioButton
                label={catsSaving ? t('admin.categories.syncing') : t('admin.categories.initDefaults')}
                className="btn-outline-secondary btn-sm"
                onClick={async () => {
                  try {
                    setCatsSaving(true);
                    setError('');
                    setSuccess('');
                    await api.initializeServices(false);
                    await fetchAll();
                    setSuccess(t('admin.categories.synced'));
                  } catch (e) {
                    setError(e.message || t('admin.categories.initFailed'));
                  } finally {
                    setCatsSaving(false);
                  }
                }}
                disabled={loading || catsSaving}
              />
              <NioButton
                label={catsSaving ? t('admin.categories.recreating') : t('admin.categories.recreate')}
                className="btn-outline-danger btn-sm"
                onClick={async () => {
                  const ok = window.confirm(t('admin.categories.recreateConfirm'));
                  if (!ok) return;
                  try {
                    setCatsSaving(true);
                    setError('');
                    setSuccess('');
                    const res = await api.adminResetServicesCatalog();
                    if (res?.success) {
                      setSuccess(res.message || t('admin.categories.recreated'));
                    }
                    await fetchAll();
                  } catch (e) {
                    setError(e.message || t('admin.categories.recreateFailed'));
                  } finally {
                    setCatsSaving(false);
                  }
                }}
                disabled={loading || catsSaving}
              />
            </div>
          </div>

          {!services.length ? (
            <div className="text-muted mb-0">{t('admin.categories.noServices')}</div>
          ) : (
            <Table responsive bordered hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>{t('admin.categories.thTitle')}</th>
                  <th>{t('admin.services.thCover')}</th>
                  <th>{t('admin.services.thSlug')}</th>
                  <th className="text-center">{t('admin.categories.thCategories')}</th>
                  <th className="text-center">{t('admin.services.thSubServices')}</th>
                  <th className="text-center">{t('admin.categories.thProviders')}</th>
                  <th className="text-center">{t('admin.services.thStatus')}</th>
                  <th className="text-center">{t('admin.services.thActions')}</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s) => (
                  <tr key={s._id || s.title}>
                    <td>
                      <span className="fw-medium">{s.title || '-'}</span>
                      {s.url && (
                        <div className="text-muted small">{s.url}</div>
                      )}
                    </td>
                    <td className="text-center">
                      {s.cover ? (
                        <img
                          src={s.cover}
                          alt={s.title || ''}
                          style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {s.slug ? (
                        <code className="small">{s.slug}</code>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      <Badge bg="light" text="dark">
                        {Array.isArray(s.categories) ? s.categories.length : 0}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge bg="info" text="dark">
                        {Array.isArray(s.subServices) ? s.subServices.length : 0}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge bg="light" text="dark">
                        {Array.isArray(s.providerIds) ? s.providerIds.length : 0}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge bg={s.isActive === false ? 'secondary' : 'success'}>
                        {s.isActive === false ? t('admin.services.inactive') : t('admin.services.active')}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1 justify-content-center flex-wrap">
                        <NioButton
                          label={t('common.edit')}
                          className="btn-outline-primary btn-sm"
                          onClick={() => openEditServiceForm(s)}
                          disabled={serviceSaving}
                        />
                        <NioButton
                          label={s.isActive === false ? t('admin.services.activate') : t('admin.services.deactivate')}
                          className={`btn-sm ${s.isActive === false ? 'btn-outline-success' : 'btn-outline-warning'}`}
                          onClick={() => toggleServiceActive(s)}
                          disabled={serviceSaving}
                        />
                        <NioButton
                          label={t('common.delete')}
                          className="btn-outline-danger btn-sm"
                          onClick={() => deleteServiceDoc(s)}
                          disabled={serviceSaving}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </NioCard.Body>
      </NioCard>

      {/* Add / Edit service modal */}
      <Modal show={showServiceForm} onHide={closeServiceForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingService ? t('admin.services.edit') : t('admin.services.addNew')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ServiceForm
            initialData={editingService}
            onSubmit={submitServiceForm}
            onCancel={closeServiceForm}
            loading={serviceSaving}
            isEdit={!!editingService}
          />
        </Modal.Body>
      </Modal>
    </>
  );

  const renderReviewsSection = () => (
    <>
      <h5 className="mb-3">{t('admin.reviews.title')}</h5>
      <p className="text-muted small mb-4">
        {t('admin.reviews.desc')}
      </p>

      <h6 className="mb-2">{t('admin.reviews.withReviews')}</h6>
      <Table responsive bordered hover className="mb-4 align-middle">
        <thead>
          <tr>
            <th>{t('admin.reviews.thClient')}</th>
            <th>{t('admin.reviews.thService')}</th>
            <th>{t('admin.reviews.thRating')}</th>
            <th>{t('admin.reviews.thComment')}</th>
            <th>{t('admin.reviews.thDate')}</th>
          </tr>
        </thead>
        <tbody>
          {requestsWithReviews.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-muted">
                {t('admin.reviews.noReviews')}
              </td>
            </tr>
          ) : (
            requestsWithReviews.map((r) => (
              <tr key={r._id}>
                <td>{r.userEmail || r.userName || '-'}</td>
                <td>{Array.isArray(r.services) && r.services[0] ? r.services[0].title : '-'}</td>
                <td>{r.averageRating ?? '-'}</td>
                <td className="text-break" style={{ maxWidth: 280 }}>
                  {r.clientComments || '-'}
                </td>
                <td>{formatTicketDate(r.updatedAt || r.createdAt)}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <h6 className="mb-2">{t('admin.reviews.providerReviews')}</h6>
      <Table responsive bordered hover className="mb-4 align-middle">
        <thead>
          <tr>
            <th>{t('admin.reviews.thDate')}</th>
            <th>{t('admin.reviews.thClient')}</th>
            <th>{t('admin.reviews.thProvider')}</th>
            <th>{t('admin.reviews.thService')}</th>
            <th>{t('admin.reviews.thRating')}</th>
            <th>{t('admin.reviews.thComment')}</th>
          </tr>
        </thead>
        <tbody>
          {providerReviews.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-muted">
                {t('admin.reviews.noProviderReviews')}
              </td>
            </tr>
          ) : (
            providerReviews.map((r) => (
              <tr key={r._id}>
                <td>{formatTicketDate(r.updatedAt || r.createdAt)}</td>
                <td>{r.userEmail || r.userName || '-'}</td>
                <td>{r.providerName || r.providerId || '-'}</td>
                <td>{r.serviceTitle || '-'}</td>
                <td>{r.rating ?? '-'}</td>
                <td className="text-break" style={{ maxWidth: 280 }}>
                  {r.comment || '-'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <h6 className="mb-2">{t('admin.reviews.savEvaluations')}</h6>
      {reviewsLoading ? (
        <div className="text-muted py-3">{t('admin.reviews.loadingTickets')}</div>
      ) : (
        <Table responsive bordered hover className="align-middle">
          <thead>
            <tr>
              <th>{t('admin.reviews.thDate')}</th>
              <th>{t('admin.reviews.thClient')}</th>
              <th>{t('admin.reviews.thProvider')}</th>
              <th>{t('admin.reviews.thSubject')}</th>
              <th>{t('admin.reviews.thRating')}</th>
              <th>{t('admin.reviews.thStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {evaluationTickets.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-muted">
                  {t('admin.reviews.noEvalTickets')}
                </td>
              </tr>
            ) : (
              evaluationTickets.map((t) => (
                <tr key={t._id}>
                  <td>{formatTicketDate(t.createdAt)}</td>
                  <td>{t.userEmail || t.userName || '-'}</td>
                  <td>{t.providerName || '-'}</td>
                  <td className="text-break" style={{ maxWidth: 200 }}>
                    {t.subject || '-'}
                  </td>
                  <td>{t.rating != null && t.rating !== '' ? String(t.rating) : '-'}</td>
                  <td>{t.status || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </>
  );

  const renderOutreachSection = () => (
    <>
      <h5 className="mb-3">{t('admin.outreach.title')}</h5>
      <div className="d-flex gap-2 mb-3">
        <NioButton
          label={loading ? t('admin.loading') : t('admin.outreach.refresh')}
          className="btn-outline-primary btn-sm"
          onClick={() => fetchAll()}
          disabled={loading}
        />
      </div>

      <h6 className="mb-2">{t('admin.outreach.contacts')}</h6>
      <p className="text-muted small mb-2">{t('admin.outreach.contactsDesc')} <code>contacts</code>.</p>
      <Table responsive bordered hover className="mb-4 align-middle">
        <thead>
          <tr>
            <th>{t('admin.outreach.thDate')}</th>
            <th>{t('admin.outreach.thName')}</th>
            <th>{t('admin.outreach.thEmail')}</th>
            <th>{t('admin.outreach.thSubject')}</th>
            <th>{t('admin.outreach.thMessage')}</th>
          </tr>
        </thead>
        <tbody>
          {!contacts.length ? (
            <tr>
              <td colSpan={5} className="text-muted">
                {t('admin.outreach.noMessages')}
              </td>
            </tr>
          ) : (
            contacts.map((c) => (
              <tr key={c._id}>
                <td>{formatTicketDate(c.createdAt)}</td>
                <td>{c.name || '-'}</td>
                <td>{c.email || '-'}</td>
                <td>{c.subject || '-'}</td>
                <td className="text-break" style={{ maxWidth: 320 }}>
                  {c.message || '-'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <h6 className="mb-2">{t('admin.outreach.blogArticles')}</h6>
      <p className="text-muted small mb-2">{t('admin.outreach.blogDesc')} <code>blogposts</code>.</p>
      <Table responsive bordered hover className="align-middle">
        <thead>
          <tr>
            <th>{t('admin.outreach.thDate')}</th>
            <th>{t('admin.outreach.thTitle')}</th>
            <th>{t('admin.outreach.thAuthor')}</th>
            <th>{t('admin.outreach.thCategory')}</th>
          </tr>
        </thead>
        <tbody>
          {!blogs.length ? (
            <tr>
              <td colSpan={4} className="text-muted">
                {t('admin.outreach.noArticles')}
              </td>
            </tr>
          ) : (
            blogs.map((b) => (
              <tr key={b._id}>
                <td>{formatTicketDate(b.createdAt)}</td>
                <td className="text-break" style={{ maxWidth: 240 }}>
                  {b.title || '-'}
                </td>
                <td>{b.author || '-'}</td>
                <td>{b.category || '-'}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </>
  );

  const renderSupportSection = () => {
    const typesAll = SAV_TYPES_KEYS;
    return (
      <>
        <h5 className="mb-3">{t('admin.support.title')}</h5>

        <Row className="gy-3 mb-3">
          <Col md={6} xl={3}>
            <NioCard><NioCard.Body className="p-3">
              <div className="text-muted small">{t('admin.support.totalTickets')}</div>
              <h4 className="mb-0">{supportStats?.total ?? supportTickets.length}</h4>
            </NioCard.Body></NioCard>
          </Col>
          <Col md={6} xl={3}>
            <NioCard className="bg-warning-soft"><NioCard.Body className="p-3">
              <div className="text-muted small">{t('admin.support.pending')}</div>
              <h4 className="mb-0">{supportStats?.pending ?? supportTickets.filter(t => t.status === 'pending').length}</h4>
            </NioCard.Body></NioCard>
          </Col>
          <Col md={6} xl={3}>
            <NioCard className="bg-info-soft"><NioCard.Body className="p-3">
              <div className="text-muted small">{t('admin.support.inProgress')}</div>
              <h4 className="mb-0">{supportStats?.inProgress ?? supportTickets.filter(t => t.status === 'in-progress').length}</h4>
            </NioCard.Body></NioCard>
          </Col>
          <Col md={6} xl={3}>
            <NioCard className="bg-success-soft"><NioCard.Body className="p-3">
              <div className="text-muted small">{t('admin.support.resolved')}</div>
              <h4 className="mb-0">{supportStats?.resolved ?? supportTickets.filter(t => t.status === 'resolved').length}</h4>
            </NioCard.Body></NioCard>
          </Col>
        </Row>

        {supportStats?.byType?.length ? (
          <NioCard className="mb-3">
            <NioCard.Body className="p-3">
              <div className="text-muted small mb-2">{t('admin.support.byType')}</div>
              <div className="d-flex flex-wrap gap-2">
                {supportStats.byType.map((row) => (
                  <span key={row.type} className="badge bg-light text-dark border">
                    {t(`admin.savTypes.${row.type}`)} : <strong className="ms-1">{row.count}</strong>
                  </span>
                ))}
              </div>
            </NioCard.Body>
          </NioCard>
        ) : null}

        <Row className="gy-2 mb-3">
          <Col md={4}>
            <Form.Label className="text-muted small text-uppercase">{t('admin.support.filterStatus')}</Form.Label>
            <Form.Select value={supportFilterStatus} onChange={(e) => setSupportFilterStatus(e.target.value)}>
              <option value="all">{t('admin.support.all')}</option>
              <option value="pending">{t('admin.savStatus.pending')}</option>
              <option value="in-progress">{t('admin.savStatus.in-progress')}</option>
              <option value="resolved">{t('admin.savStatus.resolved')}</option>
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label className="text-muted small text-uppercase">{t('admin.support.filterType')}</Form.Label>
            <Form.Select value={supportFilterType} onChange={(e) => setSupportFilterType(e.target.value)}>
              <option value="all">{t('admin.support.all')}</option>
              {typesAll.map((tk) => (
                <option key={tk} value={tk}>{t(`admin.savTypes.${tk}`)}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <NioButton
              onClick={fetchSupport}
              label={supportLoading ? t('admin.support.loading') : t('admin.support.refresh')}
              className="btn-outline-secondary btn-sm"
              disabled={supportLoading}
            />
          </Col>
        </Row>

        {supportLoading ? (
          <div className="text-muted">{t('admin.support.loading')}</div>
        ) : !filteredSupportTickets.length ? (
          <div className="text-muted">{t('admin.support.noTickets')}</div>
        ) : (
          <Table responsive bordered hover className="align-middle">
            <thead>
              <tr>
                <th>{t('admin.support.thDate')}</th>
                <th>{t('admin.support.thClient')}</th>
                <th>{t('admin.support.thType')}</th>
                <th>{t('admin.support.thSubject')}</th>
                <th>{t('admin.support.thStatus')}</th>
                <th>{t('admin.support.thPriority')}</th>
                <th>{t('admin.support.thAgent')}</th>
                <th>{t('admin.support.thMessages')}</th>
                <th>{t('admin.support.thActions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredSupportTickets.map((t) => {
                const statusKey = t.status || 'pending';
                const priorityKey = t.priority || 'medium';
                return (
                  <tr key={t._id}>
                    <td>{formatTicketDate(t.createdAt)}</td>
                    <td>{t.userName || t.userEmail}</td>
                    <td>{t(`admin.savTypes.${t.type}`) || t.type}</td>
                    <td>{t.subject}</td>
                    <td><Badge bg={SAV_STATUS_VARIANTS[statusKey]}>{t(`admin.savStatus.${statusKey}`)}</Badge></td>
                    <td><Badge bg={SAV_PRIORITY_VARIANTS[priorityKey]}>{t(`admin.savPriorities.${priorityKey}`)}</Badge></td>
                    <td>{t.assignedTo || '-'}</td>
                    <td>{Array.isArray(t.messages) ? t.messages.length : 0}</td>
                    <td className="d-flex gap-2">
                      <NioButton
                        label={t('admin.support.manage')}
                        className="btn-outline-primary btn-sm"
                        onClick={() => openSupportDetail(t)}
                      />
                      <NioButton
                        label={t('admin.support.deleteShort')}
                        className="btn-outline-danger btn-sm"
                        onClick={() => deleteSupportTicket(t)}
                        disabled={supportSaving}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        <Modal show={!!openSupportTicket} onHide={closeSupportDetail} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {openSupportTicket
                ? `${t(`admin.savTypes.${openSupportTicket.type}`) || openSupportTicket.type} — ${openSupportTicket.subject}`
                : t('admin.support.ticket')}
            </Modal.Title>
          </Modal.Header>
          {openSupportTicket && (
            <Modal.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <div className="text-muted small">{t('admin.support.customer')}</div>
                  <div>{openSupportTicket.userName || '-'}</div>
                  <div className="small text-muted">{openSupportTicket.userEmail}</div>
                  {openSupportTicket.userPhone && (
                    <div className="small text-muted">{openSupportTicket.userPhone}</div>
                  )}
                </Col>
                <Col md={6}>
                  <div className="text-muted small">{t('admin.support.createdOn')}</div>
                  <div>{formatTicketDate(openSupportTicket.createdAt)}</div>
                </Col>
                {openSupportTicket.providerName && (
                  <Col md={6} className="mt-3">
                    <div className="text-muted small">{t('admin.support.provider')}</div>
                    <div>{openSupportTicket.providerName}</div>
                  </Col>
                )}
                {openSupportTicket.requestTitle && (
                  <Col md={6} className="mt-3">
                    <div className="text-muted small">{t('admin.support.reference')}</div>
                    <div>{openSupportTicket.requestTitle}</div>
                  </Col>
                )}
                {typeof openSupportTicket.rating === 'number' && (
                  <Col md={6} className="mt-3">
                    <div className="text-muted small">{t('admin.support.rating')}</div>
                    <div>{openSupportTicket.rating} / 5</div>
                  </Col>
                )}
              </Row>

              <div className="mb-3">
                <div className="text-muted small">{t('admin.support.initialDesc')}</div>
                <div className="bg-light p-2 rounded">{openSupportTicket.description}</div>
              </div>

              {Array.isArray(openSupportTicket.attachments) && openSupportTicket.attachments.length > 0 && (
                <div className="mb-3">
                  <div className="text-muted small mb-1">{t('admin.support.attachments')}</div>
                  <div className="d-flex flex-wrap gap-2">
                    {openSupportTicket.attachments.map((a, idx) => (
                      <a key={idx} href={a} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                        {t('admin.support.attachment')} {idx + 1}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <Row className="gy-2 mb-3">
                <Col md={4}>
                  <Form.Label className="small">{t('admin.support.status')}</Form.Label>
                  <Form.Select value={supportNextStatus} onChange={(e) => setSupportNextStatus(e.target.value)} disabled={supportSaving}>
                    <option value="pending">{t('admin.savStatus.pending')}</option>
                    <option value="in-progress">{t('admin.savStatus.in-progress')}</option>
                    <option value="resolved">{t('admin.savStatus.resolved')}</option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label className="small">{t('admin.support.priority')}</Form.Label>
                  <Form.Select value={supportNextPriority} onChange={(e) => setSupportNextPriority(e.target.value)} disabled={supportSaving}>
                    <option value="low">{t('admin.savPriorities.low')}</option>
                    <option value="medium">{t('admin.savPriorities.medium')}</option>
                    <option value="high">{t('admin.savPriorities.high')}</option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label className="small">{t('admin.support.assignedAgent')}</Form.Label>
                  <Form.Control
                    value={supportAssign}
                    onChange={(e) => setSupportAssign(e.target.value)}
                    placeholder={t('admin.support.agentPlaceholder')}
                    disabled={supportSaving}
                  />
                </Col>
              </Row>

              <div className="d-flex gap-2 mb-3">
                <NioButton
                  label={supportSaving ? t('admin.support.saving') : t('admin.support.save')}
                  className="btn-primary btn-sm"
                  onClick={saveSupportTicket}
                  disabled={supportSaving}
                />
              </div>

              <hr />
              <h6>{t('admin.support.history')}</h6>
              <div className="border rounded p-2 mb-3" style={{ maxHeight: 260, overflowY: 'auto' }}>
                {!openSupportTicket.messages || openSupportTicket.messages.length === 0 ? (
                  <div className="text-muted small">{t('admin.support.noMessages')}</div>
                ) : (
                  openSupportTicket.messages.map((m, idx) => (
                    <div key={idx} className={`mb-2 p-2 rounded ${m.senderRole === 'admin' ? 'bg-primary-soft' : 'bg-light'}`}>
                      <div className="small text-muted d-flex justify-content-between">
                        <span><strong>{m.senderName || m.senderRole}</strong> ({m.senderRole})</span>
                        <span>{formatTicketDate(m.createdAt)}</span>
                      </div>
                      <div>{m.content}</div>
                    </div>
                  ))
                )}
              </div>

              <Form.Group className="mb-2">
                <Form.Label className="small">{t('admin.support.replyToCustomer')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={supportReply}
                  onChange={(e) => setSupportReply(e.target.value)}
                  placeholder={t('admin.support.replyPlaceholder')}
                  disabled={supportSaving}
                />
              </Form.Group>
              <NioButton
                label={supportSaving ? t('admin.support.sending') : t('admin.support.sendReply')}
                className="btn-primary btn-sm"
                onClick={sendSupportReply}
                disabled={supportSaving || !supportReply.trim()}
              />
            </Modal.Body>
          )}
          <Modal.Footer>
            <NioButton label={t('admin.support.close')} className="btn-outline-secondary" onClick={closeSupportDetail} />
          </Modal.Footer>
        </Modal>
      </>
    );
  };

  const renderPaymentsSection = () => {
    const paymentStatusBadge = (s) => {
      const map = {
        completed: 'success',
        pending: 'warning',
        failed: 'danger',
        expired: 'secondary',
        on_site: 'info',
      };
      return <Badge bg={map[s] || 'secondary'}>{s || '-'}</Badge>;
    };
    const paymentMethodLabel = (m) => {
      if (m === 'on_site') return t('admin.payments.onSite');
      if (m === 'card') return t('admin.payments.card');
      if (m === 'checkout') return 'Checkout';
      return m || '-';
    };
    return (
      <>
        <h5 className="mb-3">{t('admin.payments.title')}</h5>

        <Row className="gy-3 mb-3">
          <Col md={6} xl={3}>
            <NioCard><NioCard.Body className="p-3">
              <div className="text-muted small">{t('admin.payments.payments')}</div>
              <h4 className="mb-0">{paymentStats.count}</h4>
            </NioCard.Body></NioCard>
          </Col>
          <Col md={6} xl={3}>
            <NioCard className="bg-success-soft"><NioCard.Body className="p-3">
              <div className="text-muted small">{t('admin.payments.completed')}</div>
              <h4 className="mb-0">{paymentStats.completed}</h4>
            </NioCard.Body></NioCard>
          </Col>
          <Col md={6} xl={3}>
            <NioCard className="bg-warning-soft"><NioCard.Body className="p-3">
              <div className="text-muted small">{t('admin.payments.pending')}</div>
              <h4 className="mb-0">{paymentStats.pending}</h4>
            </NioCard.Body></NioCard>
          </Col>
          <Col md={6} xl={3}>
            <NioCard className="bg-info-soft"><NioCard.Body className="p-3">
              <div className="text-muted small">{t('admin.payments.revenue')}</div>
              <h4 className="mb-0">{paymentStats.total.toFixed(3)}</h4>
            </NioCard.Body></NioCard>
          </Col>
        </Row>

        <Row className="gy-2 mb-3">
          <Col md={4}>
            <Form.Label className="text-muted small text-uppercase">{t('admin.payments.filterStatus')}</Form.Label>
            <Form.Select value={paymentFilterStatus} onChange={(e) => setPaymentFilterStatus(e.target.value)}>
              <option value="all">{t('admin.payments.all')}</option>
              <option value="completed">{t('admin.payments.completed')}</option>
              <option value="pending">{t('admin.payments.pending')}</option>
              <option value="failed">Failed</option>
              <option value="expired">Expired</option>
              <option value="on_site">{t('admin.payments.onSite')}</option>
            </Form.Select>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <NioButton
              onClick={fetchPayments}
              label={paymentsLoading ? t('admin.payments.loading') : t('admin.payments.refresh')}
              className="btn-outline-secondary btn-sm"
              disabled={paymentsLoading}
            />
          </Col>
        </Row>

        {paymentsLoading ? (
          <div className="text-muted">{t('admin.payments.loading')}</div>
        ) : !filteredPayments.length ? (
          <div className="text-muted">{t('admin.payments.noPayments')}</div>
        ) : (
          <Table responsive bordered hover className="align-middle">
            <thead>
              <tr>
                <th>{t('admin.payments.thDate')}</th>
                <th>{t('admin.payments.thClient')}</th>
                <th>{t('admin.payments.thAmount')}</th>
                <th>{t('admin.payments.thCurrency')}</th>
                <th>{t('admin.payments.thDescription')}</th>
                <th>{t('admin.payments.thMethod')}</th>
                <th>{t('admin.payments.thStatus')}</th>
                <th>{t('admin.payments.thStripeRef')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((p) => (
                <tr key={p._id}>
                  <td>{formatTicketDate(p.createdAt)}</td>
                  <td>{p.userName || p.userEmail}</td>
                  <td>{Number(p.amount || 0).toFixed(3)}</td>
                  <td>{p.currency || 'TND'}</td>
                  <td>{p.description || '-'}</td>
                  <td>{paymentMethodLabel(p.paymentMethod)}</td>
                  <td>{paymentStatusBadge(p.status)}</td>
                  <td><code className="small">{p.stripeSessionId || p.stripePaymentIntentId || '-'}</code></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </>
    );
  };

  const setSettingsField = (group, field, value) => {
    setSiteSettings((prev) => ({
      ...prev,
      [group]: { ...prev[group], [field]: value },
    }));
  };

  const renderSettingsSection = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">{t('admin.settings.title')}</h5>
        <NioButton
          onClick={saveSiteSettings}
          label={settingsSaving ? t('admin.settings.saving') : t('admin.settings.save')}
          className="btn-primary btn-sm"
          disabled={settingsSaving || settingsLoading}
        />
      </div>
      {settingsLoading ? (
        <div className="text-muted">{t('admin.loading')}</div>
      ) : (
        <>
          <NioCard className="shadow-sm mb-4">
            <NioCard.Body className="p-3 p-lg-4">
              <h6 className="mb-3 border-bottom pb-2">{t('admin.settings.companyTitle')}</h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.settings.companyName')}</Form.Label>
                    <Form.Control
                      value={siteSettings.company.name}
                      onChange={(e) => setSettingsField('company', 'name', e.target.value)}
                      disabled={settingsSaving}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.settings.tagline')}</Form.Label>
                    <Form.Control
                      value={siteSettings.company.tagline}
                      onChange={(e) => setSettingsField('company', 'tagline', e.target.value)}
                      disabled={settingsSaving}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </NioCard.Body>
          </NioCard>

          <NioCard className="shadow-sm mb-4">
            <NioCard.Body className="p-3 p-lg-4">
              <h6 className="mb-3 border-bottom pb-2">{t('admin.settings.contactTitle')}</h6>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.settings.contactEmail')}</Form.Label>
                    <Form.Control
                      type="email"
                      value={siteSettings.contact.email}
                      onChange={(e) => setSettingsField('contact', 'email', e.target.value)}
                      disabled={settingsSaving}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.settings.contactPhone')}</Form.Label>
                    <Form.Control
                      value={siteSettings.contact.phone}
                      onChange={(e) => setSettingsField('contact', 'phone', e.target.value)}
                      disabled={settingsSaving}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.settings.contactAddress')}</Form.Label>
                    <Form.Control
                      value={siteSettings.contact.address}
                      onChange={(e) => setSettingsField('contact', 'address', e.target.value)}
                      disabled={settingsSaving}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </NioCard.Body>
          </NioCard>

          <NioCard className="shadow-sm mb-4">
            <NioCard.Body className="p-3 p-lg-4">
              <h6 className="mb-3 border-bottom pb-2">{t('admin.settings.socialTitle')}</h6>
              <Row>
                {['facebook', 'instagram', 'linkedin', 'twitter', 'whatsapp'].map((key) => (
                  <Col md={4} key={key}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-capitalize">{key}</Form.Label>
                      <Form.Control
                        value={siteSettings.social[key]}
                        placeholder={key === 'whatsapp' ? '+216 XX XXX XXX' : `https://${key}.com/...`}
                        onChange={(e) => setSettingsField('social', key, e.target.value)}
                        disabled={settingsSaving}
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </NioCard.Body>
          </NioCard>

          <NioCard className="shadow-sm mb-4">
            <NioCard.Body className="p-3 p-lg-4">
              <h6 className="mb-3 border-bottom pb-2">{t('admin.settings.footerTitle')}</h6>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.settings.copyright')}</Form.Label>
                    <Form.Control
                      value={siteSettings.footer.copyright}
                      placeholder={t('admin.settings.copyrightPlaceholder')}
                      onChange={(e) => setSettingsField('footer', 'copyright', e.target.value)}
                      disabled={settingsSaving}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.settings.termsUrl')}</Form.Label>
                    <Form.Control
                      value={siteSettings.footer.termsUrl}
                      onChange={(e) => setSettingsField('footer', 'termsUrl', e.target.value)}
                      disabled={settingsSaving}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.settings.privacyUrl')}</Form.Label>
                    <Form.Control
                      value={siteSettings.footer.privacyUrl}
                      onChange={(e) => setSettingsField('footer', 'privacyUrl', e.target.value)}
                      disabled={settingsSaving}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </NioCard.Body>
          </NioCard>

          <div className="d-flex justify-content-end">
            <NioButton
              onClick={saveSiteSettings}
              label={settingsSaving ? t('admin.settings.saving') : t('admin.settings.save')}
              className="btn-primary btn-sm"
              disabled={settingsSaving}
            />
          </div>
        </>
      )}
    </>
  );

  const renderSystemSection = () => {
    const db = systemStatus?.database;
    const srv = systemStatus?.server;
    const uptimeLabel = srv?.uptimeSec != null
      ? `${Math.floor(srv.uptimeSec / 3600)}h ${Math.floor((srv.uptimeSec % 3600) / 60)}m`
      : '-';
    const statusBadge = (ok, okLabel, koLabel) => (
      <Badge bg={ok ? 'success' : 'danger'}>{ok ? okLabel : koLabel}</Badge>
    );

    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{t('admin.system.title')}</h5>
          <NioButton
            onClick={fetchSystemStatus}
            label={systemLoading ? t('admin.loading') : t('admin.system.refresh')}
            className="btn-outline-secondary btn-sm"
            disabled={systemLoading}
          />
        </div>

        {/* État du système */}
        <NioCard className="shadow-sm mb-4">
          <NioCard.Body>
            <h6 className="mb-3 border-bottom pb-2">{t('admin.system.statusTitle')}</h6>
            {systemLoading && !systemStatus ? (
              <div className="text-muted">{t('admin.loading')}</div>
            ) : (
              <ul className="list-unstyled mb-0">
                <li className="d-flex justify-content-between align-items-center py-1">
                  <span>{t('admin.system.serverLabel')}</span>
                  <span className="text-end">
                    {statusBadge(systemStatus != null, t('admin.system.up'), t('admin.system.down'))}
                    {srv && (
                      <small className="text-muted ms-2">
                        :{srv.port} · Node {srv.nodeVersion} · {uptimeLabel}
                      </small>
                    )}
                  </span>
                </li>
                <li className="d-flex justify-content-between align-items-center py-1">
                  <span>{t('admin.system.dbLabel')}</span>
                  <span className="text-end">
                    {statusBadge(
                      db?.status === 'connected',
                      t('admin.system.connected'),
                      db?.status === 'connecting' ? t('admin.system.connecting') : t('admin.system.disconnected'),
                    )}
                    {db?.status === 'connected' && (
                      <small className="text-muted ms-2">
                        {db.host}:{db.port}/{db.name}
                        {db.latencyMs != null && ` · ${db.latencyMs} ms`}
                      </small>
                    )}
                  </span>
                </li>
                <li className="d-flex justify-content-between align-items-center py-1">
                  <span>{t('admin.system.stripeLabel')}</span>
                  {statusBadge(systemStatus?.stripe?.configured, t('admin.system.configured'), t('admin.system.notConfigured'))}
                </li>
                <li className="d-flex justify-content-between align-items-center py-1">
                  <span>{t('admin.system.emailLabel')}</span>
                  <span>
                    {statusBadge(systemStatus?.email?.configured, t('admin.system.configured'), t('admin.system.notConfigured'))}
                    {systemStatus?.email?.provider && (
                      <small className="text-muted ms-2">{systemStatus.email.provider}</small>
                    )}
                  </span>
                </li>
              </ul>
            )}
          </NioCard.Body>
        </NioCard>

        {/* Serveur API */}
        <NioCard className="shadow-sm mb-4">
          <NioCard.Body>
            <h6 className="mb-1 border-bottom pb-2">{t('admin.system.apiTitle')}</h6>
            <p className="text-muted small mt-2">{t('admin.system.apiDesc')}</p>
            <div className="mb-2">
              <small className="text-muted">{t('admin.system.apiCurrent')} : </small>
              <code>{effectiveApiBase}</code>{' '}
              <Badge bg={apiBaseIsOverride ? 'info' : 'secondary'}>
                {apiBaseIsOverride ? t('admin.system.apiOverride') : t('admin.system.apiDefault')}
              </Badge>
            </div>
            <Row className="g-2 align-items-end">
              <Col md={7}>
                <Form.Group>
                  <Form.Label>{t('admin.system.apiLabel')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={apiBaseInput}
                    onChange={(e) => setApiBaseInput(e.target.value)}
                    placeholder={t('admin.system.apiPlaceholder')}
                    disabled={apiBaseTesting}
                  />
                </Form.Group>
              </Col>
              <Col md={5} className="d-flex gap-2">
                <NioButton
                  onClick={testApiBaseUrl}
                  label={apiBaseTesting ? t('admin.loading') : t('admin.system.apiTest')}
                  className="btn-outline-primary btn-sm"
                  disabled={apiBaseTesting || !apiBaseInput.trim()}
                />
                <NioButton
                  onClick={applyApiBase}
                  label={t('admin.system.apiApply')}
                  className="btn-primary btn-sm"
                  disabled={apiBaseTesting || !apiBaseInput.trim()}
                />
                {apiBaseIsOverride && (
                  <NioButton
                    onClick={resetApiBase}
                    label={t('admin.system.apiReset')}
                    className="btn-outline-danger btn-sm"
                    disabled={apiBaseTesting}
                  />
                )}
              </Col>
            </Row>
            {apiBaseFeedback && (
              <div className={`alert py-2 mt-3 mb-0 ${apiBaseFeedback.ok ? 'alert-success' : 'alert-danger'}`}>
                {apiBaseFeedback.message}
              </div>
            )}
          </NioCard.Body>
        </NioCard>

        {/* Base de données */}
        <NioCard className="shadow-sm">
          <NioCard.Body>
            <h6 className="mb-1 border-bottom pb-2">{t('admin.system.dbTitle')}</h6>
            <p className="text-muted small mt-2">{t('admin.system.dbDesc')}</p>
            {db?.uriMasked && (
              <div className="mb-2">
                <small className="text-muted">{t('admin.system.dbCurrent')} : </small>
                <code>{db.uriMasked}</code>
              </div>
            )}
            <Row className="g-2 align-items-end">
              <Col md={7}>
                <Form.Group>
                  <Form.Label>{t('admin.system.dbLabel')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={dbUriInput}
                    onChange={(e) => setDbUriInput(e.target.value)}
                    placeholder={t('admin.system.dbPlaceholder')}
                    disabled={dbTesting || dbApplying}
                  />
                </Form.Group>
              </Col>
              <Col md={5} className="d-flex gap-2">
                <NioButton
                  onClick={testDatabaseUri}
                  label={dbTesting ? t('admin.loading') : t('admin.system.dbTest')}
                  className="btn-outline-primary btn-sm"
                  disabled={dbTesting || dbApplying || !dbUriInput.trim()}
                />
                <NioButton
                  onClick={applyDatabaseUri}
                  label={dbApplying ? t('admin.settings.saving') : t('admin.system.dbApply')}
                  className="btn-primary btn-sm"
                  disabled={dbTesting || dbApplying || !dbUriInput.trim()}
                />
              </Col>
            </Row>
            {dbFeedback && (
              <div className={`alert py-2 mt-3 mb-0 ${dbFeedback.ok ? 'alert-success' : 'alert-danger'}`}>
                {dbFeedback.message}
              </div>
            )}
          </NioCard.Body>
        </NioCard>

        {/* Configuration plateforme (Stripe, e-mail, URLs, auth) */}
        <NioCard className="shadow-sm mt-4">
          <NioCard.Body>
            <h6 className="mb-1 border-bottom pb-2">{t('admin.config.title')}</h6>
            <p className="text-muted small mt-2">{t('admin.config.desc')}</p>
            {configLoading && !platformConfig ? (
              <div className="text-muted">{t('admin.loading')}</div>
            ) : (
              CONFIG_GROUPS.map((group) => (
                <div key={group.key} className="mb-4">
                  <h6 className="mt-3 mb-1">{t(group.titleKey)}</h6>
                  <p className="text-muted small mb-2">{t(group.hintKey)}</p>
                  <Row className="g-3">
                    {group.fields.map((key) => {
                      const meta = platformConfig?.fields?.[key] || {};
                      const isSecret = Boolean(meta.secret);
                      const markedClear = Boolean(configClear[key]);
                      return (
                        <Col md={6} key={key}>
                          <Form.Group>
                            <Form.Label className="d-flex align-items-center gap-2">
                              {t(`admin.config.fields.${key}`)}
                              {meta.danger && (
                                <Badge bg="warning" text="dark">{t('admin.config.danger')}</Badge>
                              )}
                            </Form.Label>
                            <Form.Control
                              type={isSecret ? 'password' : 'text'}
                              autoComplete="off"
                              value={configInputs[key] ?? ''}
                              onChange={(e) => setConfigInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                              placeholder={
                                isSecret
                                  ? (meta.set ? meta.masked || t('admin.config.secretSet') : t('admin.config.notSet'))
                                  : t(`admin.config.placeholder.${key}`)
                              }
                              disabled={configSaving || markedClear}
                            />
                            <Form.Text className="text-muted">
                              {isSecret
                                ? (meta.set ? t('admin.config.secretKeep') : t('admin.config.secretEmpty'))
                                : t('admin.config.visibleEmpty')}
                              {meta.danger && ` ${t('admin.config.jwtWarning')}`}
                            </Form.Text>
                            {(isSecret || meta.set) && (
                              <Form.Check
                                type="checkbox"
                                className="mt-1 small"
                                label={t('admin.config.remove')}
                                checked={markedClear}
                                onChange={(e) => setConfigClear((prev) => ({ ...prev, [key]: e.target.checked }))}
                              />
                            )}
                          </Form.Group>
                        </Col>
                      );
                    })}
                  </Row>

                  {group.key === 'stripe' && (
                    <div className="mt-2">
                      <NioButton
                        onClick={testStripeConfig}
                        label={stripeTesting ? t('admin.loading') : t('admin.config.testStripe')}
                        className="btn-outline-primary btn-sm"
                        disabled={stripeTesting}
                      />
                      {stripeTestFeedback && (
                        <div className={`alert py-2 mt-2 mb-0 ${stripeTestFeedback.ok ? 'alert-success' : 'alert-danger'}`}>
                          {stripeTestFeedback.message}
                        </div>
                      )}
                    </div>
                  )}

                  {group.key === 'email' && (
                    <div className="mt-2">
                      <Row className="g-2 align-items-end">
                        <Col md={8}>
                          <Form.Group>
                            <Form.Label>{t('admin.config.testEmailTo')}</Form.Label>
                            <Form.Control
                              type="email"
                              value={emailTestTo}
                              onChange={(e) => setEmailTestTo(e.target.value)}
                              placeholder={getStoredUser()?.email || 'admin@example.com'}
                              disabled={emailTesting}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <NioButton
                            onClick={testEmailConfig}
                            label={emailTesting ? t('admin.loading') : t('admin.config.testEmail')}
                            className="btn-outline-primary btn-sm"
                            disabled={emailTesting}
                          />
                        </Col>
                      </Row>
                      {emailTestFeedback && (
                        <div className={`alert py-2 mt-2 mb-0 ${emailTestFeedback.ok ? 'alert-success' : 'alert-danger'}`}>
                          {emailTestFeedback.message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}

            <div className="d-flex justify-content-end">
              <NioButton
                onClick={savePlatformConfig}
                label={configSaving ? t('admin.settings.saving') : t('admin.config.save')}
                className="btn-primary"
                disabled={configSaving || configLoading}
              />
            </div>
            {configFeedback && (
              <div className={`alert py-2 mt-3 mb-0 ${configFeedback.ok ? 'alert-success' : 'alert-danger'}`}>
                {configFeedback.message}
              </div>
            )}
          </NioCard.Body>
        </NioCard>
      </>
    );
  };

  const renderActive = () => {
    if (loading) return <div className="text-muted">{t('admin.loading')}</div>;
    if (activeSection === 'users') return renderUsersSection();
    if (activeSection === 'stats') return renderStatsSection();
    if (activeSection === 'requests') return renderRequestsSection();
    if (activeSection === 'categories') return renderCategoriesSection();
    if (activeSection === 'reviews') return renderReviewsSection();
    if (activeSection === 'outreach') return renderOutreachSection();
    if (activeSection === 'payments') return renderPaymentsSection();
    if (activeSection === 'support') return renderSupportSection();
    if (activeSection === 'settings') return renderSettingsSection();
    if (activeSection === 'system') return renderSystemSection();
    return null;
  };

  return (
    <AppLayout title={t('admin.pageTitle')} rootClass="layout-1">
      <NioSection className="pt-120 pt-lg-160">
        <Row className="justify-content-center">
          <Col lg={11}>
            <NioCard className="shadow-sm">
              <NioCard.Body className="p-4 p-lg-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h2 className="mb-1">{t('admin.heading')}</h2>
                    <p className="text-muted mb-0">{t('admin.subheading')}</p>
                  </div>
                  <NioButton href="/profile" label={t('admin.backToProfile')} className="btn-outline-secondary btn-sm" />
                </div>

                {error && <div className="alert alert-danger py-2">{error}</div>}
                {success && <div className="alert alert-success py-2">{success}</div>}

                <Row>
                  <Col lg={3} className="mb-3 mb-lg-0">
                    <div className="list-group">
                      {MENU_KEYS.map((key) => (
                        <button
                          key={key}
                          type="button"
                          className={`list-group-item list-group-item-action ${activeSection === key ? 'active' : ''}`}
                          onClick={() => setActiveSection(key)}
                        >
                          {t(`admin.menu.${key}`)}
                        </button>
                      ))}
                    </div>
                  </Col>
                  <Col lg={9}>
                    {renderActive()}
                  </Col>
                </Row>
              </NioCard.Body>
            </NioCard>
          </Col>
        </Row>
      </NioSection>
    </AppLayout>
  );
}
